import { useState, useEffect } from 'react';
import { toast } from 'sonner';
import axios from 'axios';
import { Button } from '../ui/button';
import { Card } from '../ui/card';
import { Badge } from '../ui/badge';
import { Clock, CheckCircle, XCircle, Wallet, ArrowUpRight, AlertCircle, Loader2 } from 'lucide-react';
import { Input } from '../ui/input';
import { Label } from '../ui/label';
import { BACKEND_CONFIG } from '../../utils/productionConfig';

const API_URL = BACKEND_CONFIG.apiUrl;

interface PayoutRequest {
  _id: string;
  amount: number;
  status: 'pending' | 'approved' | 'rejected';
  requestedAt: string;
  processedAt?: string;
  processedBy?: string;
  notes?: string;
  rejectionReason?: string;
}

interface PayoutSectionProps {
  walletBalance: number;
  pendingEarnings: number;
  totalEarnings: number;
  onRefresh: () => void;
}

export function PayoutSection({ walletBalance, pendingEarnings, totalEarnings, onRefresh }: PayoutSectionProps) {
  const [payoutRequests, setPayoutRequests] = useState<PayoutRequest[]>([]);
  const [requestAmount, setRequestAmount] = useState('');
  const [loading, setLoading] = useState(false);
  const [requesting, setRequesting] = useState(false);
  const [showRequestForm, setShowRequestForm] = useState(false);

  // Check if there's any pending payout request
  const hasPendingRequest = payoutRequests.some(req => req.status === 'pending');

  useEffect(() => {
    fetchPayoutRequests();
  }, []);

  const fetchPayoutRequests = async () => {
    setLoading(true);
    try {
      const token = localStorage.getItem('token');
      if (!token) return;

      axios.defaults.headers.common['Authorization'] = `Bearer ${token}`;
      const { data } = await axios.get(`${API_URL}/marketing/payouts/my-requests`);

      if (data.success) {
        setPayoutRequests(data.data || []);
      }
    } catch (error: any) {
      console.error('Error fetching payout requests:', error);
      // Don't show error toast - just fail silently for better UX
      // Backend might not be available or endpoint might not exist yet
      setPayoutRequests([]);
    } finally {
      setLoading(false);
    }
  };

  const handleRequestPayout = async () => {
    const amount = parseFloat(requestAmount);

    // Validation checks
    if (!amount || amount <= 0) {
      toast.error('Please enter a valid amount');
      return;
    }

    if (amount > walletBalance) {
      toast.error(`Cannot request more than your available balance (₹${walletBalance.toLocaleString('en-IN')})`);
      return;
    }

    if (amount < 100) {
      toast.error('Minimum payout amount is ₹100');
      return;
    }

    if (hasPendingRequest) {
      toast.error('You already have a pending payout request. Please wait for it to be processed.');
      return;
    }

    setRequesting(true);
    try {
      const token = localStorage.getItem('token');
      axios.defaults.headers.common['Authorization'] = `Bearer ${token}`;

      const { data } = await axios.post(`${API_URL}/marketing/payouts/request`, {
        amount
      });

      if (data.success) {
        toast.success('Payout request submitted successfully!');
        setPayoutRequests([data.data, ...payoutRequests]);
        setRequestAmount('');
        setShowRequestForm(false);
        onRefresh();
      }
    } catch (error: any) {
      console.error('Error requesting payout:', error);
      toast.error(error.response?.data?.message || 'Failed to submit payout request');
    } finally {
      setRequesting(false);
    }
  };

  const formatCurrency = (amount: number) => {
    return `₹${amount.toLocaleString('en-IN')}`;
  };

  const formatDate = (date: string) => {
    return new Date(date).toLocaleDateString('en-IN', {
      day: 'numeric',
      month: 'short',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  const getStatusBadge = (status: string) => {
    switch (status) {
      case 'pending':
        return (
          <Badge variant="yellow">
            <Clock className="size-3" />
            Pending
          </Badge>
        );
      case 'approved':
        return (
          <Badge variant="green">
            <CheckCircle className="size-3" />
            Approved
          </Badge>
        );
      case 'rejected':
        return (
          <Badge variant="red">
            <XCircle className="size-3" />
            Rejected
          </Badge>
        );
      default:
        return null;
    }
  };

  return (
    <div className="space-y-6">
      {/* Wallet Balance Card */}
      <Card className="bg-gradient-to-br from-green-500 to-green-600 rounded-xl p-6 text-white">
        <div className="flex items-center justify-between mb-4">
          <div className="flex items-center gap-3">
            <div className="bg-white/20 rounded-full p-3">
              <Wallet className="size-6" />
            </div>
            <div>
              <p className="text-sm opacity-90">Available Balance</p>
              <div className="text-3xl font-bold">{formatCurrency(walletBalance)}</div>
            </div>
          </div>
        </div>
        
        <div className="grid grid-cols-2 gap-4 pt-4 border-t border-white/20">
          <div>
            <p className="text-xs opacity-75">Pending Approval</p>
            <p className="text-lg font-semibold">{formatCurrency(pendingEarnings)}</p>
          </div>
          <div>
            <p className="text-xs opacity-75">Total Earned</p>
            <p className="text-lg font-semibold">{formatCurrency(totalEarnings)}</p>
          </div>
        </div>

        {walletBalance >= 100 && !hasPendingRequest && (
          <Button
            onClick={() => setShowRequestForm(!showRequestForm)}
            className="w-full mt-4 bg-white text-green-600 hover:bg-white/90"
          >
            <ArrowUpRight className="size-4 mr-2" />
            Request Payout
          </Button>
        )}

        {walletBalance < 100 && (
          <div className="mt-4 bg-white/10 rounded-lg p-3 flex items-start gap-2">
            <AlertCircle className="size-4 mt-0.5 flex-shrink-0" />
            <p className="text-xs">Minimum balance of ₹100 required to request payout</p>
          </div>
        )}

        {hasPendingRequest && (
          <div className="mt-4 bg-yellow-500/20 rounded-lg p-3 flex items-start gap-2">
            <Clock className="size-4 mt-0.5 flex-shrink-0" />
            <p className="text-xs">You have a pending payout request. Please wait for approval before requesting again.</p>
          </div>
        )}
      </Card>

      {/* Request Payout Form */}
      {showRequestForm && !hasPendingRequest && (
        <Card className="bg-white rounded-xl p-6 border-2 border-green-200">
          <h3 className="text-lg font-semibold mb-4">Request Payout</h3>
          <div className="space-y-4">
            <div>
              <Label htmlFor="amount">Amount to Withdraw</Label>
              <Input
                id="amount"
                type="number"
                min="100"
                max={walletBalance}
                value={requestAmount}
                onChange={(e) => setRequestAmount(e.target.value)}
                placeholder={`Min ₹100, Max ₹${walletBalance.toLocaleString('en-IN')}`}
                className="mt-1"
              />
              <p className="text-xs text-gray-500 mt-1">
                Available balance: {formatCurrency(walletBalance)}
              </p>
            </div>

            <div className="bg-blue-50 rounded-lg p-4 border border-blue-200">
              <h4 className="text-sm font-medium text-blue-900 mb-2">Important Notes:</h4>
              <ul className="text-xs text-blue-800 space-y-1">
                <li>• Minimum payout amount is ₹100</li>
                <li>• Payouts are processed within 3-5 business days</li>
                <li>• You can only have one pending request at a time</li>
                <li>• Admin approval is required before amount is deducted</li>
              </ul>
            </div>

            <div className="flex gap-3">
              <Button
                onClick={handleRequestPayout}
                disabled={requesting}
                className="flex-1 bg-green-600 hover:bg-green-700"
              >
                {requesting ? (
                  <>
                    <Loader2 className="size-4 mr-2 animate-spin" />
                    Submitting...
                  </>
                ) : (
                  <>
                    <ArrowUpRight className="size-4 mr-2" />
                    Submit Request
                  </>
                )}
              </Button>
              <Button
                onClick={() => {
                  setShowRequestForm(false);
                  setRequestAmount('');
                }}
                variant="outline"
              >
                Cancel
              </Button>
            </div>
          </div>
        </Card>
      )}

      {/* Payout History */}
      <Card className="bg-white rounded-xl p-6">
        <h3 className="text-lg font-semibold mb-4">Payout History</h3>
        
        {loading ? (
          <div className="text-center py-8">
            <Loader2 className="size-8 animate-spin text-gray-400 mx-auto mb-3" />
            <p className="text-sm text-gray-600">Loading payout history...</p>
          </div>
        ) : payoutRequests.length === 0 ? (
          <div className="text-center py-8">
            <Wallet className="size-12 text-gray-400 mx-auto mb-3" />
            <p className="text-sm text-gray-600">No payout requests yet</p>
            <p className="text-xs text-gray-500">Request your first payout when you have earnings</p>
          </div>
        ) : (
          <div className="space-y-3">
            {payoutRequests.map((request) => (
              <div
                key={request._id}
                className="border border-gray-200 rounded-lg p-4 hover:shadow-md transition-shadow"
              >
                <div className="flex items-start justify-between mb-3">
                  <div>
                    <div className="text-2xl font-bold text-gray-900">
                      {formatCurrency(request.amount)}
                    </div>
                    <p className="text-xs text-gray-500">
                      Requested on {formatDate(request.requestedAt)}
                    </p>
                  </div>
                  {getStatusBadge(request.status)}
                </div>

                {request.status === 'approved' && request.processedAt && (
                  <div className="bg-green-50 rounded-lg p-3 mb-2">
                    <p className="text-xs text-green-800">
                      ✓ Approved on {formatDate(request.processedAt)}
                    </p>
                    {request.notes && (
                      <p className="text-xs text-green-700 mt-1">Note: {request.notes}</p>
                    )}
                  </div>
                )}

                {request.status === 'rejected' && (
                  <div className="bg-red-50 rounded-lg p-3 mb-2">
                    <p className="text-xs text-red-800">
                      ✗ Rejected {request.processedAt && `on ${formatDate(request.processedAt)}`}
                    </p>
                    {request.rejectionReason && (
                      <p className="text-xs text-red-700 mt-1">
                        Reason: {request.rejectionReason}
                      </p>
                    )}
                  </div>
                )}

                {request.status === 'pending' && (
                  <div className="bg-yellow-50 rounded-lg p-3">
                    <p className="text-xs text-yellow-800">
                      ⏳ Awaiting admin approval
                    </p>
                  </div>
                )}
              </div>
            ))}
          </div>
        )}
      </Card>
    </div>
  );
}